import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, STYLES } from '../designSystem';
import { PageContainer, PageHeader, FormCard, Button } from '../components/ui';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const ALL_US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

// ============================================================================
// DATE HELPER FUNCTIONS (copied from DealDetail.js)
// ============================================================================

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getTodayLocal() {
  return toDateStr(new Date());
}

function getNthWeekday(year, month, weekday, n) {
  const d = new Date(year, month, 1);
  let count = 0;
  while (true) {
    if (d.getDay() === weekday && ++count === n) return toDateStr(d);
    d.setDate(d.getDate() + 1);
  }
}

function getLastWeekday(year, month, weekday) {
  const d = new Date(year, month + 1, 0);
  while (d.getDay() !== weekday) d.setDate(d.getDate() - 1);
  return toDateStr(d);
}

function getUSBankHolidays(year) {
  const h = new Set();
  function obs(d) {
    const day = d.getDay();
    if (day === 0) d.setDate(d.getDate() + 1);
    else if (day === 6) d.setDate(d.getDate() - 1);
    h.add(toDateStr(d));
  }
  obs(new Date(year, 0, 1));            // New Year's Day
  h.add(getNthWeekday(year, 0, 1, 3)); // MLK Day
  h.add(getNthWeekday(year, 1, 1, 3)); // Presidents' Day
  h.add(getLastWeekday(year, 4, 1));   // Memorial Day
  obs(new Date(year, 5, 19));           // Juneteenth
  obs(new Date(year, 6, 4));            // Independence Day
  h.add(getNthWeekday(year, 8, 1, 1)); // Labor Day
  h.add(getNthWeekday(year, 9, 1, 2)); // Columbus Day
  obs(new Date(year, 10, 11));          // Veterans Day
  h.add(getNthWeekday(year, 10, 4, 4));// Thanksgiving
  obs(new Date(year, 11, 25));          // Christmas
  return h;
}

const _holidayCache = {};
function isBizDay(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  if (day === 0 || day === 6) return false;
  const year = d.getFullYear();
  if (!_holidayCache[year]) _holidayCache[year] = getUSBankHolidays(year);
  return !_holidayCache[year].has(dateStr);
}

function advanceDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return toDateStr(d);
}

function getFirstPaymentDate(disbursementDateStr, frequency) {
  if (!disbursementDateStr) return '';
  let candidate = advanceDays(disbursementDateStr, frequency === 'daily' ? 1 : 7);
  while (!isBizDay(candidate)) candidate = advanceDays(candidate, 1);
  return candidate;
}

// Helper to check required fields
const getRequiredFields = () => ({
  purchase_price: 'Purchase Price',
  revenue_purchased: 'Revenue Purchased',
  payment_frequency: 'Payment Frequency',
  num_payment_weeks: 'Number of Payment Weeks',
  state: 'State',
  disbursement_date: 'Disbursement Date',
  first_payment_date: 'First Payment Date',
});

const checkMissingRequiredFields = (data) => {
  const required = getRequiredFields();
  const missing = [];
  const dt = data.deal_terms || data;
  for (const [key, label] of Object.entries(required)) {
    if (!dt[key]) missing.push(label);
  }
  return missing;
};

export default function DealEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [deal, setDeal] = useState(null);
  const [fundingEntities, setFundingEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('draft');
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchDealData = useCallback(async () => {
    try {
      setLoading(true);
      const dealRes = await axios.get(`${API_URL}/deals/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setDeal(dealRes.data);
      setSelectedStatus(dealRes.data.status);
      setSelectedEntityId(dealRes.data.funding_entity_id || null);

      // Initialize form data from working_data
      const wd = dealRes.data.working_data || {};
      const dt = wd.deal_terms || wd;
      const di = wd.deal_identity || wd;
      const ai = wd.advanced_inputs || wd;

      const monthlyRevs = dt.monthly_revenues || [];
      setFormData({
        state: dt.state || '',
        purchase_price: dt.purchase_price || '',
        factor_rate: dt.factor_rate || '',
        revenue_purchased: dt.revenue_purchased || '',
        payment_frequency: dt.payment_frequency || 'daily',
        num_payment_weeks: dt.num_payment_weeks || '',
        disbursement_date: dt.disbursement_date || getTodayLocal(),
        first_payment_date: dt.first_payment_date || getFirstPaymentDate(dt.disbursement_date || getTodayLocal(), dt.payment_frequency || 'daily'),
        origination_fee: dt.origination_fee || '',
        broker_fee_merchant: dt.broker_fee_merchant || '',
        broker_fee_funder: dt.broker_fee_funder || '',
        refi_amount: dt.refi_amount || '',
        has_refi: dt.refi_amount > 0 || false,
        third_party_payment: dt.third_party_payment || '',
        has_third_party: dt.third_party_payment > 0 || false,
        third_party_name: ai.third_party_name || '',
        monthly_revenue_1: monthlyRevs[0] || '',
        monthly_revenue_2: monthlyRevs[1] || '',
        monthly_revenue_3: monthlyRevs[2] || '',
        monthly_revenue_4: monthlyRevs[3] || '',
        is_ny_refi: ai.is_ny_refi || false,
        ny_prior_finance_charge: ai.ny_prior_finance_charge || '',
        ny_prior_percent_paid: ai.ny_prior_percent_paid || '',
        broker_name: di.broker_name || wd.broker_name || '',
        broker_company: di.broker_company || wd.broker_company || '',
        deal_source: di.deal_source || wd.deal_source || '',
        relationship_manager: di.relationship_manager || wd.relationship_manager || '',
        internal_notes: di.internal_notes || wd.internal_notes || '',
      });

      setError('');
    } catch (err) {
      setError('Failed to load deal data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const fetchFundingEntities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/funding-entities`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setFundingEntities(response.data);
    } catch (err) {
      console.error('Failed to load funding entities', err);
    }
  }, [token]);

  useEffect(() => {
    fetchDealData();
    fetchFundingEntities();
  }, [fetchDealData, fetchFundingEntities]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setValidationError('');
  };

  // Auto-calculate revenue_purchased when purchase_price or factor_rate changes
  useEffect(() => {
    const pp = parseFloat(formData.purchase_price);
    const fr = parseFloat(formData.factor_rate);
    if (!isNaN(pp) && !isNaN(fr) && pp > 0 && fr > 0) {
      const rp = (pp * fr).toFixed(2);
      setFormData(prev => ({ ...prev, revenue_purchased: rp }));
    }
  }, [formData.purchase_price, formData.factor_rate]);

  // Auto-update first_payment_date when disbursement_date or payment_frequency changes
  useEffect(() => {
    if (formData.disbursement_date) {
      const calculated = getFirstPaymentDate(formData.disbursement_date, formData.payment_frequency || 'daily');
      setFormData(prev => ({ ...prev, first_payment_date: calculated }));
    }
  }, [formData.disbursement_date, formData.payment_frequency]);

  const handleSave = async () => {
    const missing = checkMissingRequiredFields(formData);
    if (missing.length > 0) {
      setValidationError(`Missing required fields: ${missing.join(', ')}`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Build normalized working_data structure with deal_terms and advanced_inputs
      const monthlyRevenues = [
        parseFloat(formData.monthly_revenue_1) || 0,
        parseFloat(formData.monthly_revenue_2) || 0,
        parseFloat(formData.monthly_revenue_3) || 0,
        parseFloat(formData.monthly_revenue_4) || 0,
      ];

      const workingData = {
        deal_terms: {
          state: formData.state,
          purchase_price: parseFloat(formData.purchase_price),
          factor_rate: parseFloat(formData.factor_rate) || 0,
          revenue_purchased: parseFloat(formData.revenue_purchased),
          origination_fee: parseFloat(formData.origination_fee) || 0,
          broker_fee_merchant: parseFloat(formData.broker_fee_merchant) || 0,
          broker_fee_funder: parseFloat(formData.broker_fee_funder) || 0,
          refi_amount: formData.has_refi ? (parseFloat(formData.refi_amount) || 0) : 0,
          third_party_payment: formData.has_third_party ? (parseFloat(formData.third_party_payment) || 0) : 0,
          payment_frequency: formData.payment_frequency,
          num_payment_weeks: parseInt(formData.num_payment_weeks),
          disbursement_date: formData.disbursement_date,
          first_payment_date: formData.first_payment_date,
          monthly_revenues: monthlyRevenues,
        },
        advanced_inputs: {
          is_ny_refi: formData.is_ny_refi,
          ny_prior_finance_charge: parseFloat(formData.ny_prior_finance_charge) || 0,
          ny_prior_percent_paid: parseFloat(formData.ny_prior_percent_paid) || 0,
          third_party_name: formData.has_third_party ? formData.third_party_name : '',
          reconciliation_provision: 'Section 4',
          method_of_payment: 'ACH',
          fee_description: '',
          collateral_description: '',
        },
        deal_identity: {
          broker_name: formData.broker_name || '',
          broker_company: formData.broker_company || '',
          deal_source: formData.deal_source || '',
          relationship_manager: formData.relationship_manager || '',
          internal_notes: formData.internal_notes || '',
        },
      };

      await axios.patch(`${API_URL}/deals/${id}`, {
        status: selectedStatus,
        funding_entity_id: selectedEntityId || null,
        working_data: workingData,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });

      navigate(`/deals/${id}`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save deal';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/deals/${id}`);
  };

  if (loading) {
    return (
      <PageContainer>
        <p>Loading deal...</p>
      </PageContainer>
    );
  }

  if (!deal) {
    return (
      <PageContainer>
        <p>Deal not found</p>
      </PageContainer>
    );
  }

  const paymentPeriodLabel = formData.payment_frequency === 'daily' ? 'Number of Payment Days' : 'Number of Payment Weeks';

  return (
    <PageContainer>
      <PageHeader
        title={`Edit Advance: ${deal.visual_deal_id}`}
        backLink={`/deals/${id}`}
        backText="← Back to Advance"
        primaryAction={
          <div style={{ display: 'flex', gap: SPACING.MD }}>
            <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        }
      />

      {error && <div style={{ maxWidth: '1400px', margin: '0 auto 20px', ...STYLES.errorMessage }}>{error}</div>}
      {validationError && <div style={{ maxWidth: '1400px', margin: '0 auto 20px', ...editStyles.validationError }}>{validationError}</div>}

      {/* Status Section */}
      <FormCard title="Advance Status" fullWidth>
        <div style={editStyles.formGroup}>
          <label style={editStyles.label}>Status</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={editStyles.input}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="funded">Funded</option>
            <option value="declined">Declined</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </FormCard>

      {/* Funding Entity Section */}
      <FormCard title="Funder" fullWidth>
        <div style={editStyles.formGroup}>
          <label style={editStyles.label}>Select Funder</label>
          <select
            value={selectedEntityId || ''}
            onChange={(e) => setSelectedEntityId(e.target.value === '' ? null : parseInt(e.target.value))}
            style={editStyles.input}
          >
            <option value="">-- No entity selected --</option>
            {fundingEntities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.display_name || entity.legal_name}
              </option>
            ))}
          </select>
        </div>
      </FormCard>

      {/* Required Deal Terms Section */}
      <FormCard title="Required Deal Terms" fullWidth>
        <p style={editStyles.sectionHint}>* Required fields must be filled to generate documents</p>
        <div style={editStyles.formGrid}>
          <div>
            <label style={editStyles.label}>State *</label>
            <select name="state" value={formData.state || ''} onChange={handleChange} style={editStyles.input}>
              <option value="">Select state</option>
              {ALL_US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={editStyles.label}>Purchase Price *</label>
            <input type="number" name="purchase_price" value={formData.purchase_price || ''} onChange={handleChange} style={editStyles.input} step="0.01" placeholder="Amount funder is advancing" />
          </div>
          <div>
            <label style={editStyles.label}>Factor Rate *</label>
            <input type="number" name="factor_rate" value={formData.factor_rate || ''} onChange={handleChange} style={editStyles.input} step="0.01" placeholder="e.g., 1.25" min="1" />
          </div>
          <div>
            <label style={editStyles.label}>Revenue Purchased * (auto-calculated)</label>
            <input type="number" name="revenue_purchased" value={formData.revenue_purchased || ''} onChange={handleChange} style={editStyles.input} step="0.01" placeholder="Auto-calculated from price × rate" disabled />
          </div>
          <div>
            <label style={editStyles.label}>Payment Frequency *</label>
            <select name="payment_frequency" value={formData.payment_frequency || 'daily'} onChange={handleChange} style={editStyles.input}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          <div>
            <label style={editStyles.label}>{paymentPeriodLabel} *</label>
            <input type="number" name="num_payment_weeks" value={formData.num_payment_weeks || ''} onChange={handleChange} style={editStyles.input} placeholder={formData.payment_frequency === 'daily' ? 'Total payment days' : 'Total payment weeks'} />
          </div>
          <div>
            <label style={editStyles.label}>Disbursement Date *</label>
            <input type="date" name="disbursement_date" value={formData.disbursement_date || ''} onChange={handleChange} style={editStyles.input} />
          </div>
          <div>
            <label style={editStyles.label}>First Payment Date * (auto-calculated)</label>
            <input type="date" name="first_payment_date" value={formData.first_payment_date || ''} onChange={handleChange} style={editStyles.input} disabled title="Auto-calculated based on disbursement date and frequency" />
          </div>
        </div>
      </FormCard>

      {/* Optional Deal Terms Section */}
      <FormCard title="Optional Deal Terms" fullWidth>
        <div style={editStyles.formGrid}>
          <div>
            <label style={editStyles.label}>Origination Fee</label>
            <input type="number" name="origination_fee" value={formData.origination_fee || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
          </div>
          <div>
            <label style={editStyles.label}>Broker Fee (Merchant)</label>
            <input type="number" name="broker_fee_merchant" value={formData.broker_fee_merchant || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
          </div>
          <div>
            <label style={editStyles.label}>Broker Fee (Funder)</label>
            <input type="number" name="broker_fee_funder" value={formData.broker_fee_funder || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
          </div>
          <div>
            <label style={editStyles.label}>
              <input type="checkbox" name="has_refi" checked={formData.has_refi || false} onChange={handleChange} />
              <span style={{ marginLeft: '8px' }}>Include Refi Amount</span>
            </label>
          </div>
          {formData.has_refi && (
            <div>
              <label style={editStyles.label}>Refi Amount</label>
              <input type="number" name="refi_amount" value={formData.refi_amount || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
            </div>
          )}
          <div>
            <label style={editStyles.label}>
              <input type="checkbox" name="has_third_party" checked={formData.has_third_party || false} onChange={handleChange} />
              <span style={{ marginLeft: '8px' }}>Include Third Party Payoff</span>
            </label>
          </div>
          {formData.has_third_party && (
            <>
              <div>
                <label style={editStyles.label}>Third Party Name</label>
                <input type="text" name="third_party_name" value={formData.third_party_name || ''} onChange={handleChange} style={editStyles.input} />
              </div>
              <div>
                <label style={editStyles.label}>Third Party Payment</label>
                <input type="number" name="third_party_payment" value={formData.third_party_payment || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: SPACING.LG }}>
          <h3 style={{ ...TYPOGRAPHY.H3, marginBottom: SPACING.MD }}>Monthly Revenues (Sample)</h3>
          <p style={editStyles.sectionHint}>Enter up to 4 months of revenue data for analysis</p>
          <div style={editStyles.formGrid}>
          <div>
            <label style={editStyles.label}>Month 1 Revenue</label>
            <input type="number" name="monthly_revenue_1" value={formData.monthly_revenue_1 || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
          </div>
          <div>
            <label style={editStyles.label}>Month 2 Revenue</label>
            <input type="number" name="monthly_revenue_2" value={formData.monthly_revenue_2 || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
          </div>
          <div>
            <label style={editStyles.label}>Month 3 Revenue</label>
            <input type="number" name="monthly_revenue_3" value={formData.monthly_revenue_3 || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
          </div>
          <div>
            <label style={editStyles.label}>Month 4 Revenue</label>
            <input type="number" name="monthly_revenue_4" value={formData.monthly_revenue_4 || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
          </div>
          </div>
        </div>
      </FormCard>

      {/* NY Specific Section */}
      {formData.state === 'NY' && (
        <FormCard title="NY Specific" fullWidth>
          <div style={editStyles.formGrid}>
            <div>
              <label style={editStyles.label}>
                <input
                  type="checkbox"
                  name="is_ny_refi"
                  checked={formData.is_ny_refi || false}
                  onChange={handleChange}
                />
                <span style={{ marginLeft: SPACING.SM }}>Is NY Refinance</span>
              </label>
            </div>
            <div>
              <label style={editStyles.label}>NY Prior Finance Charge</label>
              <input type="number" name="ny_prior_finance_charge" value={formData.ny_prior_finance_charge || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
            </div>
            <div>
              <label style={editStyles.label}>NY Prior Percent Paid</label>
              <input type="number" name="ny_prior_percent_paid" value={formData.ny_prior_percent_paid || ''} onChange={handleChange} style={editStyles.input} step="0.01" />
            </div>
          </div>
        </FormCard>
      )}

      {/* Broker / Deal Identity Section */}
      <FormCard title="Broker & Deal Identity (Optional)" fullWidth>
        <div style={editStyles.formGrid}>
          <div>
            <label style={editStyles.label}>Broker Name</label>
            <input type="text" name="broker_name" value={formData.broker_name || ''} onChange={handleChange} style={editStyles.input} />
          </div>
          <div>
            <label style={editStyles.label}>Broker Company</label>
            <input type="text" name="broker_company" value={formData.broker_company || ''} onChange={handleChange} style={editStyles.input} />
          </div>
          <div>
            <label style={editStyles.label}>Deal Source</label>
            <input type="text" name="deal_source" value={formData.deal_source || ''} onChange={handleChange} style={editStyles.input} />
          </div>
          <div>
            <label style={editStyles.label}>Relationship Manager</label>
            <input type="text" name="relationship_manager" value={formData.relationship_manager || ''} onChange={handleChange} style={editStyles.input} />
          </div>
        </div>
        <div style={{ marginTop: SPACING.LG }}>
          <label style={editStyles.label}>Internal Notes</label>
          <textarea
            name="internal_notes"
            value={formData.internal_notes || ''}
            onChange={handleChange}
            style={editStyles.textarea}
            rows="4"
            placeholder="Add any internal notes about this deal..."
          />
        </div>
      </FormCard>
    </PageContainer>
  );
}

const editStyles = {
  formGroup: {
    marginBottom: SPACING.LG,
  },

  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: SPACING.LG,
    marginBottom: SPACING.LG,
  },

  label: {
    ...TYPOGRAPHY.LABEL,
    color: COLORS.TEXT_PRIMARY,
    display: 'block',
    marginBottom: SPACING.SM,
  },

  input: {
    ...STYLES.input,
  },

  textarea: {
    ...STYLES.input,
    fontFamily: TYPOGRAPHY.FONT_FAMILY,
    minHeight: '120px',
    resize: 'vertical',
  },

  sectionHint: {
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_LIGHT,
    fontStyle: 'italic',
    margin: '0 0 16px 0',
  },

  validationError: {
    background: '#fffbea',
    border: `1px solid ${COLORS.WARNING}`,
    color: '#856404',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
  },
};
